import { NotImplementedError } from "@greeter/error";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type HTMLElementId = string;
type IsOpen = boolean;
type InputGroupContext = {
  groupInfo: Record<HTMLElementId, IsOpen>;
  open: (id: HTMLElementId) => void;
  close: (id: HTMLElementId) => void;
  toggle: (id: HTMLElementId) => void;
};
const InputGroupContext = React.createContext<InputGroupContext>({
  groupInfo: {},
  open() {
    throw new NotImplementedError();
  },
  close() {
    throw new NotImplementedError();
  },
  toggle() {
    throw new NotImplementedError();
  },
});

export function useInputGroup() {
  return useContext(InputGroupContext);
}

export function InputGroupProvider(props: React.PropsWithChildren) {
  const [groupInfo, setGroupInfo] = useState<Record<HTMLElementId, IsOpen>>({});

  const open = useCallback(
    (id: HTMLElementId) => {
      setGroupInfo((prev) => {
        const n = { ...prev };
        for (const key of Object.keys(n)) {
          n[key] = false;
        }
        n[id] = true;
        return n;
      });
    },
    [groupInfo]
  );

  const close = useCallback(
    (id: HTMLElementId) =>
      setGroupInfo((prev) => {
        const n = { ...prev };
        n[id] = false;
        return n;
      }),
    [groupInfo]
  );

  const toggle = useCallback(
    (id: HTMLElementId) => {
      if (groupInfo[id]) {
        close(id);
        return;
      }

      open(id);
    },
    [open, close, groupInfo]
  );

  const result = useMemo(
    () => ({ open, close, groupInfo, toggle }),
    [open, close, toggle, groupInfo]
  );

  return (
    <InputGroupContext.Provider value={result}>
      {props.children}
    </InputGroupContext.Provider>
  );
}
